'use client';
import React, { useState } from 'react';

import PnLTable from './PnLTable';
import CashFlowTable from './CashFlowTable';
import ReturnsPanel from './ReturnsPanel';
import ScenariosPanel from './ScenariosPanel';
import RiskMatrix from './RiskMatrix';
import { BarChart3, TrendingUp, DollarSign, GitBranch, Shield } from 'lucide-react';

type TabId = 'pnl' | 'cashflow' | 'returns' | 'scenarios' | 'risk';

const TABS: Array<{ id: TabId; label: string; icon: React.ReactNode }> = [
  { id: 'pnl', label: 'P&L Statement', icon: <BarChart3 size={13} /> },
  { id: 'cashflow', label: 'Cash Flow', icon: <TrendingUp size={13} /> },
  { id: 'returns', label: 'Key Returns', icon: <DollarSign size={13} /> },
  { id: 'scenarios', label: 'Scenario Analysis', icon: <GitBranch size={13} /> },
  { id: 'risk', label: 'Risk Assessment', icon: <Shield size={13} /> },
];

export default function ProjectionsContent() {
  const [activeTab, setActiveTab] = useState<TabId>('pnl');

  return (
    <div className="space-y-5">
      {/* Page header */}
      <div>
        <div className="flex items-center gap-3 mb-1">
          <div className="gold-bar h-6" />
          <h1
            style={{
              fontSize: '1.375rem',
              fontWeight: 800,
              color: 'var(--text-primary)',
              letterSpacing: '-0.02em',
            }}
          >
            Financial Projections
          </h1>
        </div>
        <p
          style={{
            fontSize: '0.65rem',
            color: 'var(--text-tertiary)',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            paddingLeft: '1rem',
          }}
        >
          10-Year P&L · Cash Flow · Returns · Scenario Analysis · Risk Matrix — all values
          recalculate from Assumptions
        </p>
      </div>

      {/* Tab bar */}
      <div
        className="flex gap-0.5 flex-wrap p-1 rounded w-fit"
        style={{ background: 'var(--surface-2)', border: '1px solid var(--divider)' }}
      >
        {TABS.map((tab) => (
          <button
            key={`tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded transition-all duration-150"
            style={{
              background: activeTab === tab.id ? 'var(--gold)' : 'transparent',
              color: activeTab === tab.id ? 'var(--primary-foreground)' : 'var(--text-tertiary)',
              fontSize: '0.65rem',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            {tab.icon}
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'pnl' && <PnLTable />}
        {activeTab === 'cashflow' && <CashFlowTable />}
        {activeTab === 'returns' && <ReturnsPanel />}
        {activeTab === 'scenarios' && <ScenariosPanel />}
        {activeTab === 'risk' && <RiskMatrix />}
      </div>
    </div>
  );
}
